import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
} from '~/components/SolaForceDataGrid';
import { EmployeesCostCenterReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';

export type CostCenter = {
  fEmpCostCenterId: number;
  fEmpCostCenterCode: string;
  fEmpCostCenterName: string;
  fEmpCostCenterPercent?: number;
};

export type EmployeesCostCenterReportRow = {
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  organizationUnit: string;
  organizationUnitNumber: string;
  costCenters: CostCenter[];
  totalPercentage: number;
};

type PropsType = {
  data: EmployeesCostCenterReportData[];
};

export const useEmployeesCostCenterReport = (props: PropsType) => {
  const { data } = props;
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const rows = useMemo(() => {
    return data.map((dataEntry): EmployeesCostCenterReportRow => {
      const { fEmpDetails, fEmpAddlCostCenters } = dataEntry;

      const costCenterDetails = fEmpDetails.fEmpCostCenterDetails as CostCenter;
      const addlCostCenters = fEmpAddlCostCenters as CostCenter[];

      const addlHasPercent = addlCostCenters.some(
        (cc) => cc.fEmpCostCenterPercent != null,
      );

      const costCenters: CostCenter[] = addlHasPercent
        ? addlCostCenters
        : [costCenterDetails];

      const totalPercentage = costCenters.reduce(
        (sum, cc) => sum + (cc.fEmpCostCenterPercent ?? 0),
        0,
      );

      return {
        employeeId: fEmpDetails.fEmpId,
        firstName: fEmpDetails.fEmpFirstName,
        lastName: fEmpDetails.fEmpLastName,
        employeeNumber: fEmpDetails.fEmpPersonNumber,
        organizationUnit: fEmpDetails.fEmpUnitDetails.fOrgUnitName,
        organizationUnitNumber: fEmpDetails.fEmpUnitDetails.fOrgUnitNumber,
        costCenters: costCenters,
        totalPercentage,
      };
    });
  }, [data]);

  const columns = useMemo((): GridColDef<EmployeesCostCenterReportRow>[] => {
    return [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        minWidth: 130,
      },
      {
        field: 'organizationUnit',
        headerName: t('fOrgUnitInputLabel'),
        minWidth: 180,
      },
      {
        field: 'organizationUnitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 110,
      },
      {
        field: 'costCenters',
        headerName: t('laCostCenters'),
        minWidth: 320,
        sortable: false,
        renderCell: (params) => {
          const costCenters = params.row.costCenters;
          return (
            <div>
              {costCenters.map((cc, index) => (
                <div key={index}>
                  {cc.fEmpCostCenterCode} {cc.fEmpCostCenterName} (
                  {cc.fEmpCostCenterPercent}%)
                </div>
              ))}
            </div>
          );
        },
      },
      {
        field: 'totalPercentage',
        headerName: t('laCostCentersTotalPercentage'),
        minWidth: 110,
        valueGetter: ({ row }) => row.totalPercentage.toFixed(1),
      },
    ];
  }, [t, goToEmployeeBasicDataView]);

  return {
    rows,
    columns,
  };
};
